import JsSIP from 'jssip';

export default class UA {

  constructor(config) {
    if (!config.webrtc) {
      console.warn("WebRTC not configured");
      return;
    }

    this._config = config;
    this._session = null;
    this._call_options = {
      eventHandlers: {
        'progress': (e) => {
          this.startRTCStatsTask();
          this.onSessionEvent("sessionInProgress", e)
        },
        'failed': (e) => { this.onSessionEvent("sessionFailed", e)},
        'ended': (e) => {
          this.stopRTCStatsTask();
          this.onSessionEvent("sessionEnded", e)
        },
        'confirmed': (e) => { this.onSessionEvent("sessionConfirmed", e)},
      },
      mediaConstraints: {audio: true, video: false },
      rtcOfferConstraints: {offerToReceiveAudio: 1, offerToReceiveVideo: 0},
    };

    this._session_events = {
      'sessionConfirmed' : [],
      'sessionInProgress' : [],
      'sessionFailed' : [],
      'sessionEnded' : [],
      'sessionOnTrack' : [],
      'sessionStats' : [],
    };

    const socket = new JsSIP.WebSocketInterface(config.webrtc.url);
    const configuration = {
      sockets  : [ socket ],
      uri      : config.webrtc.username,
      password : config.webrtc.password
    };
    this._ua = new JsSIP.UA(configuration);
    this.on("registered", this.call);
    this.on("sessionEnded", this.clearSession);
    this.on("sessionFailed", this.clearSession);

    this.rtcStatsTask = null;
    this.stats = [];
  }

  on = (event_name, event_callback) => {
    if (["sessionConfirmed",
         "sessionInProgress",
         "sessionEnded",
         "sessionFailed",
         "sessionOnTrack",
         "sessionStats"].includes(event_name)) {
      this._session_events[event_name].push(event_callback);
    } else {
      this._ua.on(event_name, event_callback);
    }
  }

  onSessionEvent = (event_name, e) => {
    this._session_events[event_name].map((cb, index) => {
      return cb(e);
    });
  }

  clearSession = (event_name, e) => {
    if (!this._session) return;
    this._session = null;
  }

  _getSIPAddress = (target) => {
    target = target.toString();
    if (target.startsWith("sip:")) return target;
    let server_url = new URL(this._config.webrtc.url);
    return "sip:" + target + "@" + server_url.hostname + ";ws=true";
  }

  isConnected = () => { return this._ua.isConnected() }

  connect = () => {
    if (this._session) return;
    if (!this._ua.isConnected()) this._ua.start();
  }

  call = () => {
    console.log("WebRTC calling");
    this._session = this._ua.call(this._getSIPAddress(this._config.exten),
                                  this._call_options);
    this._session.connection.ontrack = (e) => {
      this.onSessionEvent("sessionOnTrack", e)
    };
  }

  disconnect = () => {
    if (!this._session) return;

    this._session.terminate();
    // TODO do it on callback
    this._session = null;
  }

  close = () => {
    this._ua.stop();
  }

  setDebug = (active) => {
    if (active) JsSIP.debug.enable('JsSIP:*');
    else JsSIP.debug.disable('JsSIP:*');
  }

  onStats = (stats) => {
    this.stats.push(stats);
    this.stats = this.stats.slice(0, 2);
    let mosScore = calculateAudioScore(this.stats);
    this.onSessionEvent("sessionStats", { score: mosScore });
  }

  startRTCStatsTask = () => {
    if (this.rtcStatsTask !== null) return;

    const isInboundRTPStats = (s) => {return s.type === "inbound-rtp"}

    this.rtcStatsTask = setInterval(() => {
      this._session.connection.getStats().then((result) => {
        let stats = Array.from(result.values()).find(isInboundRTPStats);
        this.onStats(stats);
      }, (err) => {
      });
    }, 10000);
  }

  stopRTCStatsTask = () => {
    if (this.rtcStatsTask!== null) clearInterval(this.rtcStatsTask);
    this.rtcStatsTask = null;
  }
}

// https://github.com/wobbals/opentok-mos-estimator/
function calculateAudioScore(stats) {
  var audioScore = function(rtt, plr) {
    var LOCAL_DELAY = 20; //20 msecs: typical frame duration
    function H(x) { return (x < 0 ? 0 : 1) }
    var a = 0 // ILBC: a=10
    var b = 19.8
    var c = 29.7

    //R = 94.2 − Id − Ie
    var R = function(rtt, packetLoss) {
      var d = rtt + LOCAL_DELAY;
      var Id = 0.024 * d + 0.11 * (d - 177.3) * H(d - 177.3);

      var P = packetLoss;
      var Ie = a + b * Math.log(1 + c * P);

      var R = 94.2 - Id - Ie;

      return R;
    }

    //For R < 0: MOS = 1
    //For 0 R 100: MOS = 1 + 0.035 R + 7.10E-6 R(R-60)(100-R)
    //For R > 100: MOS = 4.5
    var MOS = function(R) {
      if (R < 0) {
        return 1;
      }
      if (R > 100) {
        return 4.5;
      }
      return 1 + 0.035 * R + 7.10 / 1000000 * R * (R - 60) * (100 - R);
    }

    return MOS(R(rtt, plr));
  }

  if (stats.length < 2) {
      return 0;
  }
  var currentStats = stats[stats.length - 1];
  var lastStats = stats[stats.length - 2];

  var totalAudioPackets =
  (currentStats.packetsLost - lastStats.packetsLost) +
  (currentStats.packetsReceived - lastStats.packetsReceived);
  if (0 === totalAudioPackets) {
      return 0;
  }
  var plr = (currentStats.packetsLost - lastStats.packetsLost) /
  totalAudioPackets;
  // missing from js getStats :-(
  var rtt = 0;

  var score = audioScore(rtt, plr);
  return score;
}
